/**
 * TanStack Query hooks para /v1/rtbf-requests — FWA-PWA-033.
 */
import type {
  RtbfApprovalPayload,
  RtbfCancelPayload,
  RtbfRejectPayload,
  RtbfRequest,
} from '@sisrep/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiResponse, apiGet, apiPost, generateIdempotencyKey } from '../api.js';

export interface RtbfListFilters {
  cursor?: string | undefined;
  limit?: number | undefined;
  status?: string | undefined;
  nina_entity_id?: string | undefined;
}

interface RtbfListResponse {
  data: RtbfRequest[];
  next_cursor: string | null;
  has_more: boolean;
}

export const rtbfKeys = {
  all: ['rtbf'] as const,
  list: (filters: RtbfListFilters) => ['rtbf', 'list', filters] as const,
  detail: (id: string) => ['rtbf', 'detail', id] as const,
};

function buildQueryString(filters: RtbfListFilters): string {
  const params = new URLSearchParams();
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.status) params.set('status', filters.status);
  if (filters.nina_entity_id) params.set('nina_entity_id', filters.nina_entity_id);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useRtbfRequestsList(filters: RtbfListFilters = {}) {
  return useQuery<ApiResponse<RtbfListResponse>>({
    queryKey: rtbfKeys.list(filters),
    queryFn: () => apiGet<RtbfListResponse>(`/v1/rtbf-requests${buildQueryString(filters)}`),
  });
}

export function useRtbfRequest(id: string | undefined) {
  return useQuery<ApiResponse<RtbfRequest>>({
    queryKey: rtbfKeys.detail(id ?? ''),
    queryFn: () => apiGet<RtbfRequest>(`/v1/rtbf-requests/${id}`),
    enabled: Boolean(id),
  });
}

export interface RtbfTransitionArgs<TPayload> {
  id: string;
  payload: TPayload;
  etag: string;
}

function useTransitionMutation<TPayload>(action: string) {
  const qc = useQueryClient();
  return useMutation<ApiResponse<RtbfRequest>, Error, RtbfTransitionArgs<TPayload>>({
    mutationKey: ['rtbf', action],
    mutationFn: ({ id, payload, etag }) =>
      apiPost<RtbfRequest, TPayload>(`/v1/rtbf-requests/${id}/${action}`, payload, {
        idempotencyKey: generateIdempotencyKey(),
        ifMatch: etag,
      }),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: rtbfKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: rtbfKeys.all });
    },
  });
}

export function useApproveRtbfRequest() {
  return useTransitionMutation<RtbfApprovalPayload>('approve');
}
export function useRejectRtbfRequest() {
  return useTransitionMutation<RtbfRejectPayload>('reject');
}
export function useCancelRtbfRequest() {
  return useTransitionMutation<RtbfCancelPayload>('cancel');
}
export function useExecuteRtbfRequest() {
  return useTransitionMutation<Record<string, never>>('execute');
}
